// modules/reporteCliente/pages/ReporteClienteResumenPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box, Paper, Grid, Typography, Chip, Stack, Button,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PlaceIcon from "@mui/icons-material/Place";
import PersonIcon from "@mui/icons-material/Person";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EditIcon from "@mui/icons-material/Edit";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import { buildEquiposMap, extractList } from "../../requerimiento/utils/extract";
import clienteServicioService from "../../historialCliente/services/clienteServicioService";
import HistorialCliente from "../../historialCliente/pages/HistorialCliente";
import ServicioInfoDialog from "../../clientes/components/ServicioInfoDialog";
import ContactosServicioDialog from "../../clientes/components/ContactosServicioDialog";
import InformacionFacturacionDialog from "../../clientes/components/InformacionFacturacionDialog";
import ContratosServicioDialog from "../../clientes/components/ContratosServicioDialog";
import ServicioEditDialog from "../../clientes/components/ServicioEditDialog";
import { getContactos, getContactosServicio, getContratosServicio } from "../../clientes/services/clienteService";

const MONEDAS = { 1: "Dólares", 2: "Bolivianos" };

const EQUIPOS_LABELS = {
  modem: "Modem",
  chip: "Chip",
  radio: "Radio",
  bateria: "Batería",
  varios1: "Varios 1",
  varios2: "Varios 2",
  placa: "Placa",
};

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const formatMonto = (value) => {
  const num = Number(value || 0);
  return `Bs ${num.toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 1.5,
      py: 0.55,
      borderBottom: "1px solid",
      borderColor: (theme) => alpha(theme.palette.divider, 0.6),
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem", textAlign: "right" }}>
      {value ?? "-"}
    </Typography>
  </Box>
);

const SectionPaper = ({ title, icon, color = "primary", action, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: 2,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderTop: "3px solid",
      borderTopColor: `${color}.main`,
      transition: "box-shadow 0.2s ease, transform 0.2s ease",
      "&:hover": { boxShadow: 3 },
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: (theme) => alpha(theme.palette[color].main, theme.palette.mode === "dark" ? 0.28 : 0.12),
          color: `${color}.main`,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
    </Stack>
    <Box sx={{ flex: 1 }}>{children}</Box>
    {action && (
      <Stack
        direction="row"
        justifyContent="flex-end"
        flexWrap="wrap"
        useFlexGap
        spacing={0.75}
        sx={{ mt: 1.5, pt: 1.25, borderTop: "1px solid", borderColor: "divider" }}
      >
        {action}
      </Stack>
    )}
  </Paper>
);

export default function ReporteClienteResumenPage() {
  const { cod_servicio_cliente } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = location.state?.from || "/reporte-cliente";

  const [data, setData] = useState(null);
  const [servicioCS, setServicioCS] = useState(null);
  const [equipos, setEquipos] = useState({});
  const [contactos, setContactos] = useState([]);
  const [contactoPrincipal, setContactoPrincipal] = useState(null);
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [dialogoActivo, setDialogoActivo] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [resResumen, resEquipos, resCS] = await Promise.all([
        axios.get(`/api/facturas/reporte/${cod_servicio_cliente}/resumen`),
        axios.get("/api/cliente-servicio/equipos"),
        clienteServicioService.getClienteServicio(cod_servicio_cliente),
      ]);
      const resumen = resResumen.data?.data ?? resResumen.data;
      setData(resumen);
      const map = buildEquiposMap(extractList(resEquipos?.data));
      setEquipos(map[String(cod_servicio_cliente)] || {});
      setServicioCS(resCS?.data ?? resCS);

      if (resumen?.cod_cliente) {
        try {
          const { data: contactosData } = await getContactosServicio(resumen.cod_cliente, cod_servicio_cliente);
          setContactos(Array.isArray(contactosData) ? contactosData : []);
        } catch {
          setContactos([]);
        }
        try {
          const { data: principalData } = await getContactos(resumen.cod_cliente);
          const principalList = Array.isArray(principalData) ? principalData : [];
          setContactoPrincipal(principalList[0] || null);
        } catch {
          setContactoPrincipal(null);
        }
        try {
          const { data: contratosData } = await getContratosServicio(resumen.cod_cliente, cod_servicio_cliente);
          const contratosList = Array.isArray(contratosData?.data) ? contratosData.data : Array.isArray(contratosData) ? contratosData : [];
          setContratos(contratosList);
        } catch {
          setContratos([]);
        }
      }
    } catch (e) {
      setError(e?.response?.data?.message || "No se pudo cargar el resumen del cliente.");
    } finally {
      setLoading(false);
    }
  }, [cod_servicio_cliente]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const clienteShim = servicioCS
    ? { cod_cliente: servicioCS.cod_cliente, razon_social: servicioCS.nombre_cliente }
    : null;

  const cerrarDialogo = () => setDialogoActivo(null);

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 1 }}>
          <Alert severity="error">{error || "Servicio no encontrado."}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  const equiposEntries = Object.entries(equipos).filter(([, cantidad]) => Number(cantidad) > 0);
  const contratoVigente = contratos.find((c) => c.estado === "Vigente");

  return (
    <DashboardLayout>
      <PageHeader
        title={data.razon_social}
        subtitle={`Resumen · Servicio #${data.id} · ${data.servicio_nombre}`}
        backTo={backTo}
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SectionPaper
              title="Cliente"
              icon={<PersonIcon fontSize="small" />}
              color="primary"
              action={
                <Button size="small" variant="outlined" startIcon={<InfoOutlinedIcon fontSize="small" />}
                  onClick={() => setDialogoActivo("info")}>
                  Más información
                </Button>
              }
            >
              <InfoRow label="Código cliente" value={data.cod_cliente} />
              <InfoRow label="Razón social" value={data.razon_social} />
              <InfoRow label="Rubro" value={data.rubro} />
              <InfoRow label="Departamento" value={data.departamento} />
              <InfoRow label="Fecha registro" value={formatFecha(data.cliente_fecha_registro)} />
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {(data.telefono || data.celular) && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PhoneIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {[data.telefono, data.celular].filter(Boolean).join(" / ")}
                    </Typography>
                  </Stack>
                )}
                {data.correo_ofi && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <EmailIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{data.correo_ofi}</Typography>
                  </Stack>
                )}
                {data.direccion && (
                  <Stack direction="row" spacing={0.5} alignItems="flex-start">
                    <PlaceIcon sx={{ fontSize: 15, color: "text.secondary", mt: "2px" }} />
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{data.direccion}</Typography>
                  </Stack>
                )}
              </Stack>
            </SectionPaper>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SectionPaper
              title="Servicio"
              icon={<BusinessCenterIcon fontSize="small" />}
              color="secondary"
              action={
                <Button size="small" variant="outlined" startIcon={<EditIcon fontSize="small" />}
                  onClick={() => setDialogoActivo("editar")}>
                  Editar servicio
                </Button>
              }
            >
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Chip size="small" label={data.servicio_nombre} color="primary" variant="outlined" />
                <Chip size="small" label={data.estado} color={data.estado === "Activo" ? "success" : "default"} />
                <Chip size="small" label={MONEDAS[data.moneda] || "-"} variant="outlined" />
              </Stack>
              <InfoRow label="Descripción" value={data.descripcion} />
              <InfoRow label="Tipo" value={data.tipo} />
              <InfoRow label="Grupo" value={data.grupo} />
              <InfoRow label="Sucursal" value={data.sucursal_facturar} />
              <InfoRow label="Ejecutivo de venta" value={data.ejecutivo_venta} />
              <InfoRow label="Fecha registro servicio" value={formatFecha(data.servicio_fecha_registro)} />
            </SectionPaper>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SectionPaper
              title="Facturación"
              icon={<ReceiptLongIcon fontSize="small" />}
              color="success"
              action={
                <Stack direction="row" spacing={0.75}>
                  <Button size="small" variant="outlined" startIcon={<ReceiptLongIcon fontSize="small" />}
                    onClick={() => setDialogoActivo("facturacion")}>
                    Información de facturación
                  </Button>
                  <Button size="small" variant="contained" startIcon={<VisibilityIcon fontSize="small" />}
                    onClick={() => navigate(`/reporte-cliente/${data.id}`, {
                      state: { from: `/reporte-cliente/${data.id}/resumen`, origin: backTo },
                    })}>
                    Ver reporte completo
                  </Button>
                </Stack>
              }
            >
              <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                <Box sx={{ flex: 1, textAlign: "center", py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Total facturado</Typography>
                  <Typography variant="h6" fontWeight={800} color="success.main" sx={{ lineHeight: 1.2 }}>
                    {formatMonto(data.total_facturado)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: "center", py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Saldo pendiente</Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color={data.saldo_pendiente > 0 ? "warning.main" : "text.secondary"}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {formatMonto(data.saldo_pendiente)}
                  </Typography>
                </Box>
              </Stack>
              <InfoRow label="¿Tiene factura?" value={data.tiene_factura ? "Sí" : "No"} />
              <InfoRow
                label={data.ultimo_documento ? `Última ${data.ultimo_documento.tipo}` : "Último documento"}
                value={
                  data.ultimo_documento
                    ? `${formatMonto(data.ultimo_documento.monto)} · ${formatFecha(data.ultimo_documento.fecha)}`
                    : "-"
                }
              />
            </SectionPaper>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SectionPaper
              title="Equipos"
              icon={<DevicesOtherIcon fontSize="small" />}
              color="info"
              action={
                <Button size="small" variant="outlined" startIcon={<DevicesOtherIcon fontSize="small" />}
                  onClick={() => setDialogoActivo("equipos")}>
                  Ver equipos
                </Button>
              }
            >
              {equiposEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Sin equipos registrados.</Typography>
              ) : (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {equiposEntries.map(([tipo, cantidad]) => (
                    <Chip
                      key={tipo}
                      size="small"
                      variant="outlined"
                      label={`${EQUIPOS_LABELS[tipo] || tipo}: ${cantidad}`}
                    />
                  ))}
                </Stack>
              )}
            </SectionPaper>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SectionPaper
              title="Contactos"
              icon={<ContactPhoneIcon fontSize="small" />}
              color="warning"
              action={
                <Button size="small" variant="outlined" startIcon={<ContactPhoneIcon fontSize="small" />}
                  onClick={() => setDialogoActivo("contactos")}>
                  Ver contactos
                </Button>
              }
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3 }}>
                Contacto Principal
              </Typography>
              {contactoPrincipal ? (
                <Box sx={{ mt: 0.5, mb: 1.25 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem" }}>
                    {[contactoPrincipal.titulo, contactoPrincipal.nombre, contactoPrincipal.paterno, contactoPrincipal.materno]
                      .filter(Boolean).join(" ")}
                    {contactoPrincipal.cargo_contacto ? ` · ${contactoPrincipal.cargo_contacto}` : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {[contactoPrincipal.telefono_contacto, contactoPrincipal.celular, contactoPrincipal.correo]
                      .filter(Boolean).join(" · ") || "-"}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.25 }}>
                  Sin contacto principal registrado.
                </Typography>
              )}

              <Divider sx={{ mb: 1 }} />

              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3 }}>
                Contactos
              </Typography>
              {contactos.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Sin contactos registrados.</Typography>
              ) : (
                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                  {contactos.slice(0, 4).map((c) => (
                    <Box key={c.id ?? c.id_contacto}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem" }}>
                        {c.nombre_contacto}
                        {c.cargo_contacto ? ` · ${c.cargo_contacto}` : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {[c.telefono, c.celular, c.correo].filter(Boolean).join(" · ") || "-"}
                      </Typography>
                    </Box>
                  ))}
                  {contactos.length > 4 && (
                    <Typography variant="caption" color="text.secondary">
                      +{contactos.length - 4} más...
                    </Typography>
                  )}
                </Stack>
              )}
            </SectionPaper>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SectionPaper
              title="Contratos"
              icon={<AssignmentIcon fontSize="small" />}
              color="primary"
              action={
                <Button size="small" variant="outlined" startIcon={<AssignmentIcon fontSize="small" />}
                  onClick={() => setDialogoActivo("contratos")}>
                  Contratos
                </Button>
              }
            >
              {contratoVigente ? (
                <>
                  <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                    <Chip size="small" label="Vigente" color="success" sx={{ height: 20, fontSize: "0.68rem" }} />
                  </Stack>
                  <InfoRow label="Plazo" value={contratoVigente.plazo_mes ? `${contratoVigente.plazo_mes} meses` : "-"} />
                  <InfoRow label="Garantía" value={contratoVigente.garantia} />
                  <InfoRow label="Inicio" value={formatFecha(contratoVigente.fecha_inicio)} />
                  <InfoRow label="Finalización" value={formatFecha(contratoVigente.fecha_finalizacion)} />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">Sin contrato vigente.</Typography>
              )}
            </SectionPaper>
          </Grid>
        </Grid>
      </Box>

      {servicioCS && clienteShim && dialogoActivo === "info" && (
        <ServicioInfoDialog open cliente={clienteShim} servicio={servicioCS} onClose={cerrarDialogo} />
      )}

      {dialogoActivo === "equipos" && (
        <Dialog open onClose={cerrarDialogo} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 1.25, maxHeight: "92vh" } }}>
          <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
            Equipos del servicio #{data.id}
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
              {data.razon_social} - {data.servicio_nombre}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1, bgcolor: "background.default" }}>
            <HistorialCliente codServicioClienteProp={data.id} embedded onBack={cerrarDialogo} />
          </DialogContent>
          <DialogActions sx={{ p: 0.75 }}>
            <Button size="small" onClick={cerrarDialogo}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}

      {servicioCS && clienteShim && dialogoActivo === "contactos" && (
        <ContactosServicioDialog
          open
          cliente={clienteShim}
          servicio={servicioCS}
          onClose={() => {
            cerrarDialogo();
            cargar();
          }}
        />
      )}

      {servicioCS && clienteShim && dialogoActivo === "facturacion" && (
        <InformacionFacturacionDialog open cliente={clienteShim} servicio={servicioCS} onClose={cerrarDialogo} />
      )}

      {servicioCS && clienteShim && dialogoActivo === "contratos" && (
        <ContratosServicioDialog
          open
          cliente={clienteShim}
          servicio={servicioCS}
          onClose={() => {
            cerrarDialogo();
            cargar();
          }}
        />
      )}

      {servicioCS && dialogoActivo === "editar" && (
        <ServicioEditDialog
          open
          servicio={servicioCS}
          onClose={cerrarDialogo}
          onSaved={() => {
            cerrarDialogo();
            cargar();
          }}
        />
      )}
    </DashboardLayout>
  );
}
